import { Injectable } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { CacheService } from '@df/core/cache/cache';
import { Status } from '@df/core/status';
import { GoogleMaps } from '@df/module-google-maps/google-maps.namespace';
import { GoogleMapsService } from '@df/module-google-maps/google-maps.service';
import uniq from 'lodash-es/uniq';
import { MetapackCollectionPoint } from './metapack-collection-point';
import { Metapack } from './metapack.namespace';

@Injectable({
    providedIn: 'root'
})
export class MetapackService {
    private index: {
        [key: string]: MetapackCollectionPoint;
    } = {};

    constructor(protected request: RequestService, protected cache: CacheService, private googleMapsService: GoogleMapsService) {}

    /**
     * Returns collection point by booking code
     * @param {string} bookingCode
     * @returns {MetapackCollectionPoint | undefined}
     */
    get(bookingCode: string): MetapackCollectionPoint | undefined {
        return this.index[bookingCode];
    }

    /**
     * Finds collection points by geolocation coordinates
     * @returns {IPromise<MetapackCollectionPoint[]>}
     */
    findByGeolocation(
        status: Status | undefined,
        data: Metapack.IFindByGeolocationData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<MetapackCollectionPoint[]> {
        return this.find(status, data, onSuccess, onError);
    }

    /**
     * Finds collection points by postcode
     * @returns {IPromise<MetapackCollectionPoint[]>}
     */
    findByPostcode(
        status: Status | undefined,
        data: Metapack.IFindByPostcodeData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<MetapackCollectionPoint[]> {
        return this.find(status, data, onSuccess, onError);
    }

    /**
     * Finds one collection points by postcode and unique booking code
     * @returns {IPromise<MetapackCollectionPoint[]>}
     */
    findOne(
        status: Status | undefined,
        data: Metapack.IFindOne,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<MetapackCollectionPoint[]> {
        return this.find(status, data, onSuccess, onError);
    }

    findByAddress(
        status: Status | undefined,
        data: GoogleMaps.ILocateRequest,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<MetapackCollectionPoint[]> {
        return this.request.send(
            status,
            () =>
                new Promise((resolve, reject) =>
                    this.googleMapsService
                        .geocode(data)
                        .then(results => {
                            const result = results[0];
                            const latitude = result?.latitude;
                            const longitude = result?.longitude;
                            if (latitude && longitude) {
                                this.findByGeolocation(
                                    undefined,
                                    { latitude, longitude },
                                    metapackCollectionPoints => resolve({ data: metapackCollectionPoints }),
                                    reject
                                );
                            } else {
                                reject();
                            }
                        })
                        .catch(reject)
                ),
            onSuccess,
            onError
        );
    }

    /**
     * Finds metapack collection points
     * @param {Status} status
     * @param {Metapack.IFindByGeolocationData | Metapack.IFindByPostcodeData | Metapack.IFindOne} data
     * @param {IRequestCallback} onSuccess
     * @param {IRequestCallback} onError
     * @returns {IPromise<MetapackCollectionPoint[]>}
     */
    protected find(
        status: Status | undefined,
        data: Metapack.IFindByGeolocationData | Metapack.IFindByPostcodeData | Metapack.IFindOne,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<MetapackCollectionPoint[]> {
        return this.request.send(
            status,
            () =>
                new Promise((resolve, reject) => {
                    const cacheName = 'metapack.find';

                    const cached = this.cache.get([cacheName, data]);
                    if (cached) {
                        resolve({ data: cached });
                    } else {
                        this.request.send(
                            status,
                            { path: Metapack.FIND_ENDPOINT, data },
                            (response: Metapack.ICollectionPointData | Metapack.ICollectionPointData[]) => {
                                const loaded = Array.isArray(response) ? response : [response];
                                const collectionPoints = uniq(loaded).map(
                                    collectionPointData => new MetapackCollectionPoint(collectionPointData)
                                );
                                this.cache.set([cacheName, data], collectionPoints);
                                if (collectionPoints.length) {
                                    collectionPoints.forEach(cp => {
                                        this.index[cp.data.booking_code] = cp;
                                    });
                                }
                                resolve({ data: collectionPoints });
                            },
                            reject
                        );
                    }
                }),
            onSuccess,
            onError
        );
    }
}
